import React from "react";
import { Button } from "react-bootstrap";
import AvatarImage from "../../ui/Avatar/AvatarImage";
import NoDataFound from "./NoDataFound";
import TaskAction from "../../../interfaces/task/taskActions/TaskAction";
import { ROLES } from "../../../Data/SD";
import { User } from "firebase/auth";

interface TaskActivitySectionProps {
  activities: TaskAction[] | null | undefined;
  currentUser: User | null;
  currentRoles: { name: string }[] | null;
  formatActivityTime: (date: Date | string | undefined) => string;
  onEditActivity: (activity: TaskAction) => void;
  onAddActivity: () => void;
}

// Configuration for Activity Types
// Maps the API's "actionSubTypeName" to a friendly Label and a CSS Class
const ACTIVITY_TYPE_CONFIG: Record<string, { label: string; badgeClass: string }> = {
  "TASK SUBMISSION": { label: "Submission", badgeClass: "bg-green-100 text-green-800" },
  "TASK APPROVAL": { label: "Approved", badgeClass: "bg-green-100 text-green-800" },
  "TASK COMPLETED": { label: "Completed", badgeClass: "bg-gray-800 text-white" },
  "FILE UPLOAD": { label: "File Uploaded", badgeClass: "bg-blue-100 text-blue-800" },
  "TASK FILE DELETED": { label: "File Deleted", badgeClass: "bg-red-100 text-red-800" },
  "REJECTED": { label: "Rejected", badgeClass: "bg-red-100 text-red-800" },
  "TASK EXTENTION": { label: "Extension Requested", badgeClass: "bg-yellow-100 text-yellow-800" },
  "TASK CLOSURE REQUEST": { label: "Closure Requested", badgeClass: "bg-orange-100 text-orange-800" },
  // Default fallback
  "DEFAULT": { label: "Update", badgeClass: "bg-gray-100 text-gray-800" },
  "EXTERNAL FILE REQUEST SENT": { label: "External File Request Sent", badgeClass: "bg-purple-100 text-purple-800" },
  "EXTERNAL FILE UPLOADED": {
    label: "External File Uploaded",
    badgeClass: "bg-blue-100 text-blue-800"
  },
  "EXTERNAL FILE ACCEPTED": {
    label: "External File Approved",
    badgeClass: "bg-green-100 text-green-800"
  },
  "EXTERNAL FILE REJECTED": {
    label: "External File Rejected",
    badgeClass: "bg-red-100 text-red-800"
  },
  "EXTERNAL FILE REVOKED": {
    label: "External File Revoked",
    badgeClass: "bg-gray-300 text-gray-800"
  },

};

const TaskActivitySection: React.FC<TaskActivitySectionProps> = ({
  activities,
  currentUser,
  currentRoles,
  formatActivityTime,
  onEditActivity,
  onAddActivity,
}) => {
  
  const getActivityBadge = (subTypeName: string | undefined) => {
    const normalizedType = subTypeName?.toUpperCase() || "DEFAULT";
    const config = ACTIVITY_TYPE_CONFIG[normalizedType] || ACTIVITY_TYPE_CONFIG["DEFAULT"];
    
    // Fallback label if config not found but subtype exists
    const displayLabel = config === ACTIVITY_TYPE_CONFIG["DEFAULT"] && subTypeName 
      ? subTypeName 
      : config.label;

    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.badgeClass}`}>
        {displayLabel}
      </span>
    );
  };

  const getAvatarBorderColor = (subTypeName: string | undefined): string | null => {
    const normalizedType = subTypeName?.toUpperCase() || "DEFAULT";
    const config = ACTIVITY_TYPE_CONFIG[normalizedType] || ACTIVITY_TYPE_CONFIG["DEFAULT"];
    
    // Only show border for green or red badges
    const greenOrRedBadges = ["bg-green-100 text-green-800", "bg-red-100 text-red-800"];
    
    if (!greenOrRedBadges.includes(config.badgeClass)) {
      return null; // No border for non-green/red badges
    }
    
    // Map badge colors to border colors (only green and red)
    const borderColorMap: Record<string, string> = {
      "bg-green-100 text-green-800": "#10b981", // green-500
      "bg-red-100 text-red-800": "#ef4444", // red-500
    };

    return borderColorMap[config.badgeClass] || null;
  };

  return (
    <div className="activity-feed">
      {activities && activities.length > 0 ? (
        <div className="activity-feed-container">
          <ul>
            {activities.map((item) => (
            <li key={item.id} className="activity-item">
              <div className="activity-avatar">
                <div
                  style={{
                    borderRadius: "50%",
                    border: getAvatarBorderColor(item.actionSubType?.actionSubTypeName) 
                      ? `1px solid ${getAvatarBorderColor(item.actionSubType?.actionSubTypeName)}` 
                      : "1px solid #ffffff",
                    padding: getAvatarBorderColor(item.actionSubType?.actionSubTypeName) ? "2px" : "0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AvatarImage 
                      userId={item.author?.id ?? ""} 
                      size="sm"
                    />
                  </div>
                </div>
              </div>
              <div className="activity-content w-full">
                <div className="activity-header flex items-center gap-2 flex-wrap">
                  <span className="author-name font-semibold text-gray-900">
                    {(item.author?.firstName || "System") + " " + (item.author?.lastName || "")}
                  </span>
                  
                  {/* Middle Dot Separator */}
                  <span className="text-gray-400">•</span>

                  {/* The New Colored Badge */}
                  {getActivityBadge(item.actionSubType?.actionSubTypeName)}

                  <span className="activity-time text-gray-500 text-xs ml-auto">
                    {formatActivityTime(item.createDate)}
                  </span>
                </div>
                
                <p className="activity-description mt-1 text-gray-600">
                  {item.description}
                </p>
              </div>

              {/* Edit Button for PMs */}
              {currentRoles?.some((r) => r.name === ROLES.PROGRAM_MANAGER) && (
                <span
                  aria-label="edit activity"
                  className="edit-activity-btn cursor-pointer text-xs text-blue-600 hover:text-blue-800 ml-2"
                  onClick={() => onEditActivity(item)}
                >
                  Edit
                </span>
              )}
            </li>
          ))}
          </ul>
        </div>
      ) : (
        <NoDataFound
          title="No Activity Found"
          description="There is no activity to show you right now"
        />
      )}
      <Button className="add-activity-btn mt-3" onClick={onAddActivity}>
        Add Activity
      </Button>
    </div>
  );
};

export default TaskActivitySection;